import { useState, useRef, useCallback } from 'react'
import { ChevronDown, Settings, HelpCircle, SlidersHorizontal, PanelLeft, PanelLeftClose } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useConnectionStore } from '@/stores/connectionStore'
import { useNavVisibilityStore } from '@/stores/navVisibilityStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { PHASES, TOOLS, ADVANCED_ITEMS, getPhaseStatus } from './sidebarNav'
import { SidebarPhaseItem, SidebarPhaseConnector } from './SidebarPhaseItem'

interface Props {
  activeView: string
  onViewChange: (view: string) => void
  toggleModeButton?: React.ReactNode
}

export function SidebarJourneyNav({ activeView, onViewChange, toggleModeButton }: Props) {
  const { isConnected, isConnecting } = useConnectionStore()
  const { isPinned, togglePinned } = useNavVisibilityStore()
  const phaseProgress = useJourneyStore(s => s.journey?.phaseProgress ?? {})
  const [isHovered, setIsHovered] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(null)

  const isExpanded = isPinned || isHovered

  const activePhase = PHASES.find(p => p.viewIds.includes(activeView)) ?? null
  const activePhaseId = activePhase?.id ?? null

  const handleMouseEnter = useCallback(() => {
    if (isPinned) return
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 200)
  }, [isPinned])

  const handleMouseLeave = useCallback(() => {
    if (isPinned) return
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    setIsHovered(false)
  }, [isPinned])

  return (
    <aside
      className={cn(
        'h-full flex flex-col flex-shrink-0 bg-warm-50 transition-all duration-200 z-20',
        isPinned
          ? 'w-52 relative border-r border-warm-200'
          : isHovered
            ? 'w-52 absolute left-0 top-0 bottom-0 shadow-lg border-r border-warm-200'
            : 'w-14 relative'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Top toggle — always visible */}
      <div className={cn(
        'flex items-center flex-shrink-0 border-b border-warm-200 py-2',
        isExpanded ? 'px-3 justify-between' : 'justify-center px-1'
      )}>
        {isExpanded && (
          <span className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">
            Your journey
          </span>
        )}
        <button
          type="button"
          onClick={togglePinned}
          title={isPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
          className="w-7 h-7 flex items-center justify-center rounded text-text-muted hover:bg-warm-200/60 hover:text-text transition-colors"
        >
          {isPinned
            ? <PanelLeftClose className="w-4 h-4" />
            : <PanelLeft className="w-4 h-4" />
          }
        </button>
      </div>

      <div className="flex flex-col gap-0.5 px-1.5 py-3 overflow-y-auto flex-1">
        {/* Phases */}
        <div className={cn('flex flex-col', isExpanded ? '' : 'items-center')}>
          {PHASES.map((phase, idx) => {
            const livePhase = { ...phase, progress: phaseProgress[phase.id] ?? 0 }
            return (
              <div key={phase.id}>
                <SidebarPhaseItem
                  phase={livePhase}
                  status={getPhaseStatus(livePhase, activePhaseId)}
                  onClick={() => onViewChange(phase.defaultView)}
                  isExpanded={isExpanded}
                />
                {idx < PHASES.length - 1 && isExpanded && <SidebarPhaseConnector />}
              </div>
            )
          })}
        </div>

        <div className="flex-1 min-h-4" />

        {/* Tools */}
        {isExpanded && (
          <div className="px-2.5 pt-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-text-muted">
            Tools
          </div>
        )}
        <div className={cn('mt-1 flex flex-col gap-0.5', !isExpanded && 'items-center')}>
          {TOOLS.map(tool => {
            const Icon = tool.icon
            const isActive = activeView === tool.view
            return (
              <button
                key={tool.id}
                type="button"
                onClick={() => onViewChange(tool.view)}
                title={!isExpanded ? tool.label : undefined}
                className={cn(
                  'relative flex items-center rounded-md text-left transition-colors',
                  isExpanded ? 'gap-2.5 px-2.5 py-2 w-full' : 'justify-center w-9 h-9',
                  isActive ? 'bg-warm-100' : 'hover:bg-warm-200/40'
                )}
              >
                {isActive && isExpanded && (
                  <span aria-hidden className="absolute left-0 top-2 bottom-2 w-[2px] rounded-sm bg-accent" />
                )}
                <Icon
                  className="h-4 w-4 flex-shrink-0"
                  style={{ color: isActive ? 'var(--color-text)' : 'var(--color-text-secondary)' }}
                />
                {isExpanded && (
                  <span className={cn('text-[13px] truncate', isActive ? 'font-medium text-text' : 'text-text-secondary')}>
                    {tool.label}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* More tools */}
        <div className="mt-1">
          <button
            type="button"
            onClick={() => isExpanded ? setAdvancedOpen(!advancedOpen) : undefined}
            title={!isExpanded ? 'More tools' : undefined}
            className={cn(
              'flex items-center rounded-md text-left transition-colors hover:bg-warm-200/40',
              isExpanded ? 'w-full gap-2.5 px-2.5 py-2' : 'justify-center w-9 h-9 mx-auto'
            )}
          >
            <SlidersHorizontal className="h-4 w-4 flex-shrink-0 text-text-secondary" />
            {isExpanded && (
              <>
                <span className="text-[13px] flex-1 text-text-secondary">More tools</span>
                <ChevronDown className={cn('h-3.5 w-3.5 flex-shrink-0 text-text-muted transition-transform', !advancedOpen && '-rotate-90')} />
              </>
            )}
          </button>
          {isExpanded && advancedOpen && (
            <ul className="mt-0.5 flex flex-col">
              {ADVANCED_ITEMS.map(item => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onViewChange(item.id)}
                    className={cn(
                      'w-full rounded-md px-9 py-1.5 text-left text-[12px] transition-colors',
                      activeView === item.id
                        ? 'bg-warm-100 text-text font-medium'
                        : 'text-text-secondary hover:bg-warm-200/40'
                    )}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Settings / Help */}
        <div className={cn('mt-2 flex flex-col gap-0.5', !isExpanded && 'items-center')}>
          {([
            { id: 'settings', label: 'Settings', Icon: Settings },
            { id: 'help', label: 'Help', Icon: HelpCircle },
          ] as const).map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => onViewChange(id)}
              title={!isExpanded ? label : undefined}
              className={cn(
                'flex items-center rounded-md text-left transition-colors hover:bg-warm-200/40',
                isExpanded ? 'gap-2.5 px-2.5 py-2 w-full' : 'justify-center w-9 h-9',
                activeView === id && 'bg-warm-100 font-medium'
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0 text-text-secondary" />
              {isExpanded && <span className="text-[13px] text-text-secondary">{label}</span>}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className={cn(
          'mt-2 flex items-center pt-2 border-t border-warm-200',
          isExpanded ? 'px-2.5 justify-between' : 'flex-col justify-center gap-1'
        )}>
          <div className="flex items-center gap-2">
            <div className={cn(
              'w-2 h-2 rounded-full',
              isConnecting ? 'bg-warning animate-pulse' : isConnected ? 'bg-success' : 'bg-text-muted'
            )} />
            {isExpanded && (
              <span className="text-[11px] text-text-muted">
                {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Offline'}
              </span>
            )}
          </div>
          {isExpanded && toggleModeButton}
        </div>
      </div>
    </aside>
  )
}
